import React, { useEffect, useMemo, useState, useRef } from 'react';
import { motion, AnimatePresence, useReducedMotion, useScroll, useTransform, useSpring, useMotionValueEvent } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { useSuppressFloatingCta } from './floatingCtaContext';
import { buildBrain, IntegrationSphere, LogoStrip } from './SlackIntegrationGraph';
import { TaskFeedPanel } from './TaskFeedPanel';
// ─────────────────────────────────────────────────────────────────────────
// CHAT-TRANSITION VARIANT
// A scroll-driven twin of <SlackIntegrationGraph>, identical through Act One
// (the spinning integration sphere + "Giga plugs into your world") and Act Two
// (the "Works right in your chats" chat panel). The hand-off is intentionally
// simple: the sphere transitions OUT, a large "@Giga …" prompt types itself,
// and then the chat panel builds in — Giga simply appears as the panel's own
// avatar. There is NO travelling logo and NO circle→square morph in between.
//
// Lives only on /chat-transition. The production home choreography is untouched.
// ─────────────────────────────────────────────────────────────────────────
// The three positionings that swap one at a time under the title as the user
// scrolls — framing exactly the kind of work Giga is for: the things you
// can't get to, keep putting off, and shouldn't have to do.
const POSITIONINGS = ['can’t get to', 'keep putting off', 'shouldn’t have to do'];
export function SlackIntegrationGraphCardMorph() {
  const reduceMotion = useReducedMotion();
  const brain = useMemo(() => buildBrain(), []);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 767px)');
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);
  const {
    scrollYProgress
  } = useScroll({
    target: scrollRef,
    offset: ['start end', 'end end']
  });
  const smoothProgress = useSpring(scrollYProgress, {
    stiffness: 90,
    damping: 26,
    mass: 0.5
  });
  // ── ACT ONE — title card + sphere (identical to production) ──────────────
  // The title holds noticeably longer now so the rotating positioning sub-line
  // can step through all three frames at a comfortable pace before the block
  // hands off to the sphere — it never blinks away mid-rotation.
  const introOpacity = useTransform(scrollYProgress, [0, 0.08, 0.16, 0.38, 0.42], [0, 0, 1, 1, 0]);
  const introY = useTransform(scrollYProgress, [0.08, 0.16, 0.38, 0.42], [24, 0, 0, -40]);
  const introScale = useTransform(scrollYProgress, [0.08, 0.16, 0.38, 0.42], [0.96, 1, 1, 0.9]);
  // The positioning sub-line reveals a beat AFTER the title lands: first you
  // read "Giga plugs into your world.", then on the next scroll the line
  // "It does the work you can't get to" rises in and begins rotating.
  const subOpacity = useTransform(scrollYProgress, [0.18, 0.23], [0, 1]);
  const subY = useTransform(scrollYProgress, [0.18, 0.23], [14, 0]);
  // The web fades in only AFTER the headline has fully cleared, so the core
  // and the big headline never share the screen — that overlap was what read
  // as "messy and cheap".
  const sphereOpacity = useTransform(scrollYProgress, [0, 0.4, 0.46, 0.5, 0.56], [0, 0, 1, 1, 0]);
  // The sphere ENTERS large and zooms OUT as you scroll — pulling back to
  // reveal the whole network condensing toward the Giga core — rather than
  // holding at a fixed size. By the time it has shrunk, the logos + core have
  // dissolved into a clean field of white network dots.
  const sphereScale = useTransform(scrollYProgress, [0.4, 0.48, 0.56], isMobile ? [1.3, 0.95, 0.78] : [2.3, 1.45, 1.05]);
  // The dark stage now holds LONGER — through the whole typed-prompt beat — and
  // only warms to cream as the prompt is flying into the chat composer.
  const bgOpacity = useTransform(scrollYProgress, [0.78, 0.9], [1, 0]);
  // ── ACT 1.5 — THE TYPED PROMPT ───────────────────────────────────────────
  // Between the sphere transitioning out and the chat building, a LARGE
  // "@Giga …" prompt types itself out as you scroll — the "someone is assigning
  // Giga a job" beat. It then fades + lifts away as the chat panel builds in.
  const PROMPT_MENTION = '@Giga';
  const PROMPT_BODY = ', give me a summary of what you’ve done in the last 24 hours.';
  const PROMPT_FULL = PROMPT_MENTION + PROMPT_BODY;
  // Character count driven by scroll: starts typing as the sphere leaves (~0.5)
  // and finishes very quickly (~0.56) so it feels like fast typing.
  const typedCountRaw = useTransform(scrollYProgress, [0.5, 0.56], [0, PROMPT_FULL.length]);
  const [typedCount, setTypedCount] = useState(0);
  useMotionValueEvent(typedCountRaw, 'change', v => {
    setTypedCount(Math.max(0, Math.min(PROMPT_FULL.length, Math.round(v))));
  });
  // The big prompt block: fades in as the sphere leaves, holds while typing,
  // then fades + lifts + shrinks slightly as it "commits" into the composer.
  const promptOpacity = useTransform(scrollYProgress, [0.47, 0.52, 0.68, 0.74], [0, 1, 1, 0]);
  const promptScale = useTransform(scrollYProgress, [0.52, 0.68, 0.74], [1, 1, 0.82]);
  const promptY = useTransform(scrollYProgress, [0.68, 0.74], [0, 40]);
  // ── ACT TWO — chat ───────────────────────────────────────────────────────
  // Grows in from a touch-small as it's revealed, so the panel reads as
  // materializing in place rather than sliding/fading in from somewhere else.
  const rawChatScale = useTransform(scrollYProgress, [0.82, 0.88, 0.96], isMobile ? [0.94, 1, 1.04] : [0.72, 0.92, 1.04]);
  const chatScale = useSpring(rawChatScale, {
    stiffness: 140,
    damping: 22,
    mass: 0.4
  });
  // The chat panel stays hidden while the typed prompt is on screen, then
  // resolves over [0.82, 0.845] right as the prompt lifts away — so the chat
  // (with Giga's avatar) simply appears once the sphere + prompt have cleared.
  const chatOpacity = useTransform(scrollYProgress, [0.82, 0.845], [0, 1]);
  const chatHeadingOpacity = useTransform(scrollYProgress, [0.76, 0.84], [0, 1]);
  const chatHeadingY = useTransform(scrollYProgress, [0.76, 0.84], [16, 0]);
  const [active, setActive] = useState(false);
  // While the title card is on screen, step the positioning sub-line through
  // the three frames one at a time as you scroll. The switches are spread
  // evenly across the (now wider) visible band so each phrase gets a real
  // beat — including the last one — before the block lifts away at ~0.38.
  const [positionIndex, setPositionIndex] = useState(0);
  useMotionValueEvent(scrollYProgress, 'change', v => {
    const idx = v >= 0.32 ? 2 : v >= 0.26 ? 1 : 0;
    setPositionIndex(prev => prev === idx ? prev : idx);
  });
  // Lift the spinning web + its Giga core well above the viewport midline so
  // the core sits cleanly ABOVE the centered "Giga plugs into your world."
  // headline during the brief cross-fade, instead of colliding with the text.
  const STAGE_LIFT = typeof window !== 'undefined' ? window.innerHeight * 0.16 : 120;
  // Suppress the global floating CTA during the integration beat.
  const [inIntegrationBeat, setInIntegrationBeat] = useState(false);
  useMotionValueEvent(scrollYProgress, 'change', latest => {
    if (!inIntegrationBeat && latest >= 0.3 && latest < 0.84) {
      setInIntegrationBeat(true);
    } else if (inIntegrationBeat && (latest >= 0.86 || latest < 0.26)) {
      setInIntegrationBeat(false);
    }
  });
  useSuppressFloatingCta(!reduceMotion && inIntegrationBeat);
  return <div ref={scrollRef} className="relative w-full" style={{
    height: reduceMotion ? undefined : '300vh'
  }} data-id="element-5607" data-mp="3sfcdanm6x4mm">
      <section className="sticky top-0 h-screen w-full overflow-hidden flex flex-col bg-[#FAF8F4]" data-id="element-5608" data-mp="jzixkazonx6m">
        <motion.div aria-hidden="true" className="pointer-events-none absolute inset-0" style={{
        opacity: reduceMotion ? 0 : bgOpacity,
        background: '#FAF8F4'
      }} data-id="element-5609" data-mp="osv8xjfcq7hf" />
        <motion.div aria-hidden="true" className="pointer-events-none absolute inset-0" style={{
        opacity: reduceMotion ? 0 : bgOpacity,
        backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.6'/%3E%3C/svg%3E\")",
        mixBlendMode: 'multiply',
        opacity: 0.03
      }} data-id="element-5610" data-mp="2g36xkgqjzpi0" />

        {/* Atmosphere — faint constellation dot-field that foreshadows the
         network sphere, fading out toward the edges. */}
        <motion.div aria-hidden="true" className="pointer-events-none absolute inset-0" style={{
        opacity: reduceMotion ? 0 : bgOpacity
      }} data-id="element-5611" data-mp="1jer2y68l6py1">
          <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(rgba(43,33,26,0.1) 0.5px, transparent 0.9px)',
          backgroundSize: '34px 34px',
          WebkitMaskImage: 'radial-gradient(ellipse 70% 60% at 50% 50%, #000 25%, transparent 80%)',
          maskImage: 'radial-gradient(ellipse 70% 60% at 50% 50%, #000 25%, transparent 80%)'
        }} data-id="element-5612" data-mp="1egza9a04azss" />
        </motion.div>

        {/* Atmosphere — warm ambient glow behind the headline for depth and
         warmth, breathing slowly so the stage feels alive. */}
        <motion.div aria-hidden="true" className="pointer-events-none absolute inset-0 flex items-center justify-center" style={{
        opacity: reduceMotion ? 0 : bgOpacity
      }} data-id="element-5613" data-mp="1ryz6tvhut84f">
          <motion.div className="w-[120vw] h-[120vw] max-w-[1100px] max-h-[1100px] rounded-full" style={{
          background: 'radial-gradient(circle, rgba(234,88,12,0.12) 0%, rgba(234,88,12,0.04) 32%, transparent 65%)',
          filter: 'blur(20px)'
        }} animate={reduceMotion ? undefined : {
          scale: [1, 1.08, 1],
          opacity: [0.85, 1, 0.85]
        }} transition={reduceMotion ? undefined : {
          duration: 9,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-5614" data-mp="21xez41smanfi" />
        </motion.div>

        {/* Atmosphere — full-edge vignette to focus the eye on the center. */}
        <motion.div aria-hidden="true" className="pointer-events-none absolute inset-0" style={{
        opacity: reduceMotion ? 0 : bgOpacity,
        background: 'radial-gradient(ellipse 75% 75% at 50% 50%, transparent 40%, rgba(250,248,244,0.8) 100%)'
      }} data-id="element-5615" data-mp="1t8ie3fhi8m86" />

        <motion.div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-[34%]" style={{
        opacity: reduceMotion ? 0 : introOpacity,
        background: 'linear-gradient(to bottom, rgba(250,248,244,0.8), rgba(250,248,244,0) 100%)'
      }} data-id="element-5616" data-mp="27w3idcq48rho" />

        {/* ACT ONE — title card */}
        <motion.div className="pointer-events-none absolute inset-0 z-30 flex flex-col items-center justify-center text-center px-6" style={{
        opacity: reduceMotion ? 0 : introOpacity,
        y: reduceMotion ? 0 : introY,
        scale: reduceMotion ? 1 : introScale
      }} data-id="element-5617" data-mp="2lvlcm7m6kwyf">
          <h2 className="max-w-4xl text-4xl sm:text-6xl md:text-7xl font-title font-medium tracking-tight leading-[1.05] text-[#2B211A]" style={{
          textShadow: 'none'
        }} data-id="element-5618" data-mp="2m1lwajls0elv">
            Giga plugs into your world.
          </h2>

          {/* Positioning sub-line — reveals after the title, then swaps one
           frame at a time as you keep scrolling (it never disappears
           between frames; only the phrase changes). */}
          <motion.p className="mt-5 sm:mt-7 max-w-3xl text-xl sm:text-3xl md:text-4xl font-title font-medium tracking-tight leading-tight text-[#2B211A]/65" style={{
          opacity: reduceMotion ? 1 : subOpacity,
          y: reduceMotion ? 0 : subY
        }} data-id="element-5619" data-mp="34z6n3w83pn94">
            and does the work you{' '}
            {reduceMotion ? <span className="text-[#EA580C] font-semibold" data-id="element-5620" data-mp="3sor80knteul1">
                can’t get to, keep putting off, or shouldn’t have to do.
              </span> : <span className="relative inline-block min-w-[1ch] align-baseline" data-id="element-5621" data-mp="1vsehki0ux0wv">
                <AnimatePresence mode="wait" initial={false} data-id="element-5622" data-mp="1h3s30ij20d6p">
                  <motion.span key={positionIndex} initial={{
                opacity: 0,
                y: '0.45em'
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: '-0.45em'
              }} transition={{
                duration: 0.35,
                ease: [0.22, 1, 0.36, 1]
              }} className="inline-block text-[#EA580C] font-semibold whitespace-nowrap" data-id="element-5623" data-mp="1h15uogoc9tnf">
                    {POSITIONINGS[positionIndex]}.
                  </motion.span>
                </AnimatePresence>
              </span>}
          </motion.p>
        </motion.div>

        {/* ACT ONE — integration sphere */}
        <motion.div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center" onViewportEnter={() => setActive(true)} style={{
        opacity: reduceMotion ? 0 : sphereOpacity,
        scale: reduceMotion ? 1 : sphereScale,
        y: reduceMotion ? 0 : -STAGE_LIFT
      }} data-id="element-5624" data-mp="2xgp6z75pza76">
          <IntegrationSphere brain={brain} smoothProgress={smoothProgress} active={active || !!reduceMotion} reduceMotion={!!reduceMotion} showCore theme="light" data-id="element-5625" data-mp="vm1a73rodo5j" />
        </motion.div>

        {/* ACT ONE — logo strip */}
        <motion.div className="pointer-events-none absolute inset-x-0 bottom-[16%] sm:bottom-[15%] z-30 flex justify-center px-6" style={{
        opacity: reduceMotion ? 0 : sphereOpacity
      }} data-id="element-5626" data-mp="10j1d55t8aasf">
          <LogoStrip reduceMotion={!!reduceMotion} theme="light" data-id="element-5627" data-mp="19jz9r31c7g0z" />
        </motion.div>

        {/* ACT 1.5 — the large typed "@Giga …" prompt. Sits just below the
         lifted Giga mark (which reads as the avatar), types itself out with
         scroll, then commits down into the chat composer. */}
        {!reduceMotion && <motion.div className="pointer-events-none absolute inset-0 z-40 flex items-center justify-center px-6" style={{
        opacity: promptOpacity,
        y: promptY,
        scale: promptScale
      }} data-id="element-5628" data-mp="3ct1a2jd2a0rm">
            <p className="max-w-4xl text-center text-3xl sm:text-5xl md:text-6xl font-title font-medium leading-snug tracking-tight text-[#2B211A]" data-id="element-5629" data-mp="3mrh34rvnfk3r">
              <span className="text-[#EA580C] font-semibold" data-id="element-5630" data-mp="3rn61wszugwz0">
                {PROMPT_FULL.slice(0, Math.min(typedCount, PROMPT_MENTION.length))}
              </span>
              <span className="text-[#2B211A]" data-id="element-5631" data-mp="2z1cwebn5z48j">
                {typedCount > PROMPT_MENTION.length ? PROMPT_FULL.slice(PROMPT_MENTION.length, typedCount) : ''}
              </span>
              <motion.span aria-hidden="true" className="inline-block w-[3px] h-[1em] -mb-[0.1em] ml-1 bg-[#EA580C] align-middle" animate={{
            opacity: [1, 1, 0, 0]
          }} transition={{
            duration: 1,
            repeat: Infinity,
            ease: 'linear'
          }} data-id="element-5632" data-mp="38rppis91o5qa" />
            </p>
          </motion.div>}

        {/* ACT TWO — chat headline */}
        <motion.div className="pointer-events-none absolute top-[8%] sm:top-[10%] left-0 right-0 z-30 text-center px-6" style={{
        opacity: reduceMotion ? 1 : chatHeadingOpacity,
        y: reduceMotion ? 0 : chatHeadingY
      }} data-id="element-5633" data-mp="td48jhbncshm">
          <span className="block text-xs font-semibold tracking-[5px] uppercase text-neutral-400 mb-3 md:mb-4" data-id="element-5634" data-mp="jey34pqmt480">
            Your personal chief of staff
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-title font-medium tracking-tight text-[#2B211A]" data-id="element-5635" data-mp="9pej3gr6n4bk">
            Handles work and life.
          </h2>
        </motion.div>

        {/* ACT TWO — task feed panel */}
        <motion.div className="absolute inset-x-0 top-[23%] sm:top-[28%] z-30 flex justify-center px-4" style={{
        transformOrigin: 'center top',
        scale: reduceMotion ? 1 : chatScale,
        opacity: reduceMotion ? 1 : chatOpacity
      }} data-id="element-5636" data-mp="qjh6gv68ge2">
          <TaskFeedPanel scrollProgress={reduceMotion ? undefined : scrollYProgress} data-id="element-5637" data-mp="3d81bbj8tnmtz" />
        </motion.div>

        {/* Scroll hint — Act One only */}
        <motion.div className="pointer-events-none absolute bottom-6 left-1/2 -translate-x-1/2 z-40 flex flex-col items-center gap-1.5 text-[#2B211A]/40" style={{
        opacity: reduceMotion ? 0 : introOpacity
      }} data-id="element-5638" data-mp="3i8yr5p6d04sb">
          <span className="text-[11px] font-semibold uppercase tracking-[3px]" data-id="element-5639" data-mp="38bau2sla6l6z">
            Scroll
          </span>
          <motion.span animate={{
          y: [0, 6, 0]
        }} transition={{
          duration: 1.6,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-5640" data-mp="4shk0prpoehk">
            <ChevronDown size={18} strokeWidth={2.25} data-id="element-5641" data-mp="3n4xlruia6j16" />
          </motion.span>
        </motion.div>
      </section>
    </div>;
}